// useTimezonePref — preferred timezone for billing/reset timestamps.
//
// Two-tier persistence so the preference behaves well for both signed-
// in and signed-out users:
//
//   1. localStorage  — instant, used for the first paint and as the
//      sole storage for guests. Cross-tab synced via the storage event.
//   2. user_preferences.timezone (Postgres) — when the user is signed
//      in we hydrate from the DB on mount and write back on every
//      change, so the preference follows them across devices and
//      sessions. The DB row is the source of truth when we have one;
//      localStorage just mirrors it so the next visit (or a tab opened
//      before the network resolves) doesn't flash the browser default.
//
// API is unchanged: `{ tz, setTz, resolved }`. Callers don't need to
// know whether the value came from disk or from Postgres.
import { useCallback, useEffect, useRef, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";

const STORAGE_KEY = "hl_tz_pref";
/** Sentinel meaning "follow the browser / OS". */
export const TZ_AUTO = "auto" as const;
/** Sentinel meaning "always render in UTC" (matches old behavior). */
export const TZ_UTC = "UTC" as const;

export type TzPref = string; // "auto" | "UTC" | IANA zone like "America/New_York"

const readPref = (): TzPref => {
  if (typeof window === "undefined") return TZ_AUTO;
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    return raw && raw.length > 0 ? raw : TZ_AUTO;
  } catch {
    return TZ_AUTO;
  }
};

/**
 * Resolve the preference to an actual IANA zone string Intl can use.
 * `auto` → the browser's detected zone; `UTC` stays as `UTC`; any other
 * value is passed through.
 */
export const resolveTz = (pref: TzPref): string => {
  if (pref === TZ_AUTO) {
    try {
      return Intl.DateTimeFormat().resolvedOptions().timeZone || "UTC";
    } catch {
      return "UTC";
    }
  }
  return pref || "UTC";
};

/**
 * Format a Date in the user's preferred timezone with a short, friendly
 * label suffix (e.g. "Dec 1, 12:00 AM EST" / "Dec 1, 00:00 UTC").
 */
export const formatInTz = (
  date: Date,
  pref: TzPref,
  opts: Intl.DateTimeFormatOptions = {
    month: "short",
    day: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  },
): string => {
  const timeZone = resolveTz(pref);
  const base = date.toLocaleString(undefined, { ...opts, timeZone });
  // Append the short zone label so the user knows which clock they're
  // looking at without having to hover for a tooltip.
  let zoneLabel = timeZone;
  try {
    const parts = new Intl.DateTimeFormat(undefined, {
      timeZone,
      timeZoneName: "short",
    }).formatToParts(date);
    const tzPart = parts.find((p) => p.type === "timeZoneName");
    if (tzPart?.value) zoneLabel = tzPart.value;
  } catch {
    /* fall through to IANA name */
  }
  return `${base} ${zoneLabel}`;
};

/**
 * Return just the short timezone abbreviation for the user's preferred
 * zone at the given moment (e.g. "PDT", "EST", "UTC", "GMT+2"). Useful
 * for compact UI like meter chips where we don't have room for the
 * full timestamp but still want the user to see *which clock* a
 * "refills in 3d 2h" countdown is anchored to.
 *
 * Falls back to the IANA zone name (or "UTC") if the runtime can't
 * produce a short label.
 */
export const getTzAbbr = (pref: TzPref, date: Date = new Date()): string => {
  const timeZone = resolveTz(pref);
  try {
    const parts = new Intl.DateTimeFormat(undefined, {
      timeZone,
      timeZoneName: "short",
    }).formatToParts(date);
    const tzPart = parts.find((p) => p.type === "timeZoneName");
    if (tzPart?.value) return tzPart.value;
  } catch {
    /* fall through */
  }
  return timeZone || "UTC";
};

export function useTimezonePref() {
  const [tz, setTzState] = useState<TzPref>(() => readPref());
  const { user } = useAuth();
  // Tracks the user we last hydrated for so we don't keep re-hydrating
  // on every render or fight an in-flight write with a stale read.
  const hydratedFor = useRef<string | null>(null);
  // Skip the very next DB write that would be triggered by a setTz()
  // we issued ourselves while hydrating from the DB — otherwise the
  // hydration would echo back to the server immediately.
  const skipNextWrite = useRef(false);

  // Cross-tab sync: another tab changing the pref should update here too.
  useEffect(() => {
    const onStorage = (e: StorageEvent) => {
      if (e.key === STORAGE_KEY) {
        setTzState(e.newValue && e.newValue.length > 0 ? e.newValue : TZ_AUTO);
      }
    };
    window.addEventListener("storage", onStorage);
    return () => window.removeEventListener("storage", onStorage);
  }, []);

  // Hydrate from Postgres on sign-in. The DB wins over local cache so
  // a user who set their zone on another device sees the right value
  // here on first load. We still keep the local cache populated for
  // first-paint speed and offline.
  useEffect(() => {
    if (!user?.id) {
      hydratedFor.current = null;
      return;
    }
    if (hydratedFor.current === user.id) return;
    hydratedFor.current = user.id;
    let cancelled = false;
    (async () => {
      try {
        const { data, error } = await supabase
          .from("user_preferences")
          .select("timezone")
          .eq("user_id", user.id)
          .maybeSingle();
        if (cancelled || error || !data?.timezone) return;
        const remote = data.timezone as TzPref;
        // Only update + cache if the remote value actually differs
        // from what we have. Avoids a needless re-render and an echo
        // write back to the server.
        if (remote !== tz) {
          skipNextWrite.current = true;
          setTzState(remote);
          try {
            window.localStorage.setItem(STORAGE_KEY, remote);
          } catch {
            /* private mode — fine, in-memory state still correct */
          }
        }
      } catch {
        /* network or RLS hiccup — local value stays in effect */
      }
    })();
    return () => {
      cancelled = true;
    };
    // We intentionally don't depend on `tz` here — that would re-fire
    // the hydration every time the user changes their preference.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.id]);

  const setTz = useCallback((next: TzPref) => {
    setTzState(next);
    try {
      window.localStorage.setItem(STORAGE_KEY, next);
    } catch {
      /* private mode — preference is session-only */
    }
    // Mirror to Postgres so the preference follows the user across
    // devices. Upsert by user_id; the table already has per-user RLS
    // so this is a no-op for guests and safe for signed-in users.
    if (skipNextWrite.current) {
      skipNextWrite.current = false;
      return;
    }
    const userId = user?.id;
    if (!userId) return;
    void supabase
      .from("user_preferences")
      .upsert(
        { user_id: userId, timezone: next },
        { onConflict: "user_id" },
      )
      .then(({ error }) => {
        if (error) {
          // Swallow — local cache already holds the value, so the UI
          // is correct for this session even if the round-trip failed.
          console.warn("[useTimezonePref] failed to persist", error.message);
        }
      });
  }, [user?.id]);

  return { tz, setTz, resolved: resolveTz(tz) };
}
